import { apiClient } from '@/shared/lib/apiClient';
import type {
  ApiResponse,
  GuideBankAccount,
  GuideWallet,
  PaginatedResponse,
  WalletSummary,
  WalletTransaction,
  WithdrawalRequest,
} from '@/types';

export const guideFinanceApi = {
  async getWallet() {
    const res = await apiClient.get<ApiResponse<GuideWallet>>('/guide/wallet');
    return res.data.data!;
  },

  async getSummary() {
    const res = await apiClient.get<ApiResponse<WalletSummary>>('/guide/wallet/summary');
    return res.data.data!;
  },

  async getTransactions(params?: {
    page?: number;
    limit?: number;
    transactionType?: string;
    status?: string;
  }) {
    const res = await apiClient.get<PaginatedResponse<WalletTransaction>>(
      '/guide/wallet/transactions',
      { params }
    );
    return { data: res.data.data, pagination: res.data.pagination };
  },

  async listBankAccounts() {
    const res = await apiClient.get<ApiResponse<GuideBankAccount[]>>('/guide/bank-accounts');
    return res.data.data!;
  },

  async createBankAccount(payload: {
    accountHolderName: string;
    accountNumber: string;
    ifscCode: string;
    bankName: string;
    branch?: string;
    upiId?: string;
    isPrimary?: boolean;
  }) {
    const res = await apiClient.post<ApiResponse<GuideBankAccount>>('/guide/bank-accounts', payload);
    return res.data.data!;
  },

  async setPrimaryBankAccount(id: string) {
    const res = await apiClient.patch<ApiResponse<GuideBankAccount>>(
      `/guide/bank-accounts/${id}/set-primary`
    );
    return res.data.data!;
  },

  async deleteBankAccount(id: string) {
    await apiClient.delete(`/guide/bank-accounts/${id}`);
  },

  async createWithdrawal(payload: { amount: number; bankAccountId: string; remarks?: string }) {
    const res = await apiClient.post<ApiResponse<WithdrawalRequest>>('/guide/withdrawals', payload);
    return res.data.data!;
  },

  async listWithdrawals(params?: { page?: number; limit?: number; status?: string }) {
    const res = await apiClient.get<PaginatedResponse<WithdrawalRequest>>('/guide/withdrawals', {
      params,
    });
    return { data: res.data.data, pagination: res.data.pagination };
  },
};

export const guideFinanceKeys = {
  all: ['guide-finance'] as const,
  wallet: () => [...guideFinanceKeys.all, 'wallet'] as const,
  summary: () => [...guideFinanceKeys.all, 'summary'] as const,
  transactions: (params?: object) => [...guideFinanceKeys.all, 'transactions', params] as const,
  bankAccounts: () => [...guideFinanceKeys.all, 'bank-accounts'] as const,
  withdrawals: (params?: object) => [...guideFinanceKeys.all, 'withdrawals', params] as const,
};
